/**
 * Renders a user's avatar with a graceful fallback. Google profile
 * URLs (lh3.googleusercontent.com/...) and LinkedIn CDN URLs go stale
 * — the image returns 403/404 weeks after the user signed up. Plain
 * <img src=...> shows a broken-image icon in that case. We swap to
 * the initial-on-flame-gradient fallback on `onError` instead.
 *
 * Source-of-truth fallback: avatar field absent OR the loaded image
 * fired error → render the initial.
 */

import { useState } from 'react';

interface Props {
  /** Avatar URL stored on the user (Google / LinkedIn / S3). */
  src: string | null | undefined;
  /** Display name; first letter feeds the gradient fallback. */
  name?: string | null;
  /** Email used when name is empty. */
  email?: string | null;
  /** Tailwind size + radius classes — caller decides scale. */
  className?: string;
  /** Fallback text size — separate so the same component scales for
   *  16px (topbar) up through 64px (profile page). */
  fallbackTextClass?: string;
}

export function UserAvatar({
  src,
  name,
  email,
  className = 'h-full w-full',
  fallbackTextClass = 'text-xs',
}: Props) {
  const [errored, setErrored] = useState(false);
  const initial = (name?.[0] ?? email?.[0] ?? 'U').toUpperCase();
  if (!src || errored) {
    return (
      <div
        className={`${className} flex items-center justify-center font-bold text-white ${fallbackTextClass}`}
        style={{
          backgroundImage:
            'linear-gradient(to bottom right, color-mix(in oklab, var(--color-accent) 80%, #000), var(--color-accent))',
        }}
      >
        {initial}
      </div>
    );
  }
  return (
    <img
      src={src}
      alt=""
      onError={() => setErrored(true)}
      className={`${className} object-cover`}
    />
  );
}
